/**
 * Static prototype evaluator service — backed by the in-memory mock store.
 * Public method signatures match the original API client.
 */

import {
  Evaluator,
  EvaluatorApplication,
  EvaluatorApplicationDetails,
  Evaluation,
  CreateEvaluationRequest,
  SubmitEvaluationResponse,
  SetupPasswordRequest,
  PasswordSetupResponse,
  RequestPasswordSetupRequest,
  RequestPasswordSetupResponse,
  ConflictDeclarationRequest,
  ConflictDeclarationResponse,
  DeclineAssignmentRequest,
  InvitationDetails,
  InvitationResponseRequest,
  InvitationResponseResponse,
  EvaluatorServiceError,
} from '../types/evaluator';
import { db, delay, newId, persist } from '../mocks/store';
import {
  filesForApp,
  findApp,
  findUser,
  toEvaluator,
} from '../mocks/mappers';
import { StoredAssignment, StoredEvaluation } from '../mocks/seed';
import authService from './authService';

const currentEvaluatorId = (): string =>
  authService.getCurrentUser()?.id ?? '22222222-2222-4222-8222-00000000000B';

const projectTitleOf = (appId: string): string => {
  const app = findApp(appId);
  return (
    app?.sectionAnswers?.PROJECT_INFORMATION?.projectTitle ??
    app?.applicationNumber ??
    'Postulación'
  );
};

const principalInvestigatorOf = (appId: string): string => {
  const app = findApp(appId);
  const pi = app?.sectionAnswers?.PROJECT_TEAM?.principalInvestigator;
  if (pi?.name) return pi.name;
  return findUser(app?.userId ?? '')?.name ?? 'Investigador/a';
};

const toEvaluation = (e: StoredEvaluation): Evaluation => ({
  id: e.id,
  assignmentId: e.assignmentId,
  criteriaEvaluations: e.criteriaEvaluations,
  budgetComments: e.budgetComments,
  overallComments: e.overallComments,
  conflictsDeclared: e.conflictsDeclared,
  conflictDetails: e.conflictDetails,
  submittedAt: e.submittedAt,
  createdAt: e.createdAt,
  updatedAt: e.updatedAt,
});

const toEvaluatorApplication = (a: StoredAssignment): EvaluatorApplication => {
  const app = findApp(a.applicationId);
  const evaluation = db.evaluations.find((e) => e.assignmentId === a.id);
  return {
    id: a.applicationId,
    applicationNumber: app?.applicationNumber ?? '',
    status: app?.status ?? 'under_evaluation',
    projectTitle: projectTitleOf(a.applicationId),
    principalInvestigator: principalInvestigatorOf(a.applicationId),
    submittedAt: app?.submittedAt ?? null,
    assignment: {
      id: a.id,
      assignedAt: a.assignedAt,
      acceptedAt: a.acceptedAt ?? (a.status === 'pending' ? null : a.assignedAt),
      status: a.status,
      evaluationSubmitted: !!a.evaluationSubmittedAt,
      conflictDeclared: a.conflictDeclared,
      conflictStatus: a.conflictStatus,
      conflictDetails: a.conflictDetails,
      conflictDeclaredAt: a.conflictDeclaredAt,
      declineReason: (a as any).declineReason ?? null,
      declineNote: (a as any).declineNote ?? null,
    },
    evaluation: evaluation
      ? {
          id: evaluation.id,
          submittedAt: evaluation.submittedAt,
        }
      : undefined,
  };
};

const findAssignment = (
  applicationId: string,
  evaluatorId: string
): StoredAssignment | undefined =>
  db.assignments.find(
    (a) => a.applicationId === applicationId && a.evaluatorId === evaluatorId
  );

// Unified conflict mechanism: declining for conflict of interest is a
// conflict declaration, not a plain decline. The admin sees a single,
// consistent signal regardless of where the conflict was reported.
const applyDeclineToAssignment = (
  assignment: StoredAssignment,
  reason: 'thematic' | 'conflict' | 'other',
  note?: string
): void => {
  if (reason === 'conflict') {
    assignment.status = 'conflict_declared';
    assignment.conflictDeclared = true;
    assignment.conflictStatus = 'conflict_declared';
    assignment.conflictDetails = note?.trim() || null;
    assignment.conflictDeclaredAt = new Date().toISOString();
  } else {
    assignment.status = 'declined';
    (assignment as any).declineReason = reason;
    (assignment as any).declineNote = note?.trim() || null;
  }
};

class EvaluatorService {
  async getProfile(): Promise<Evaluator> {
    const user = findUser(currentEvaluatorId());
    if (!user) {
      throw new EvaluatorServiceError('Perfil de evaluador no encontrado', 404);
    }
    return delay(toEvaluator(user));
  }

  async updateProfile(_profileData: any): Promise<Evaluator> {
    const user = findUser(currentEvaluatorId());
    if (!user) {
      throw new EvaluatorServiceError('Perfil de evaluador no encontrado', 404);
    }
    return delay(toEvaluator(user), 200);
  }

  async getAssignedApplications(): Promise<EvaluatorApplication[]> {
    const evaluatorId = currentEvaluatorId();
    const apps = db.assignments
      .filter((a) => a.evaluatorId === evaluatorId)
      .map(toEvaluatorApplication);
    return delay(apps);
  }

  async getApplicationDetails(
    applicationId: string
  ): Promise<EvaluatorApplicationDetails> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    const app = findApp(applicationId);
    if (!assignment || !app) {
      throw new EvaluatorServiceError('Aplicación no encontrada', 404);
    }
    const base = toEvaluatorApplication(assignment);
    const user = findUser(app.userId);
    const details: EvaluatorApplicationDetails = {
      ...base,
      answers: { sectionAnswers: app.sectionAnswers, sectionStatus: app.sectionStatus },
      createdAt: app.createdAt,
      updatedAt: app.updatedAt,
      user: { id: app.userId, name: user?.name ?? 'Usuario', email: user?.email ?? '' },
      files: filesForApp(applicationId).map((f) => ({
        id: f.id,
        filename: f.filename,
        originalName: f.originalName,
        mimeType: f.mimeType,
        size: f.size,
        sectionType: f.sectionType,
        uploadedAt: f.uploadedAt.toISOString(),
      })),
    };
    return delay(details);
  }

  async getApplicationFiles(applicationId: string): Promise<
    Array<{
      id: string;
      filename: string;
      originalName: string;
      mimeType: string;
      size: number;
      sectionType: string;
      uploadedAt: string;
    }>
  > {
    const files = filesForApp(applicationId).map((f) => ({
      id: f.id,
      filename: f.filename,
      originalName: f.originalName,
      mimeType: f.mimeType,
      size: f.size,
      sectionType: f.sectionType as string,
      uploadedAt: f.uploadedAt.toISOString(),
    }));
    return delay(files);
  }

  async getEvaluation(applicationId: string): Promise<Evaluation | null> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    if (!assignment) return delay(null);
    const evaluation = db.evaluations.find((e) => e.assignmentId === assignment.id);
    return delay(evaluation ? toEvaluation(evaluation) : null);
  }

  async createEvaluation(
    applicationId: string,
    evaluationData: CreateEvaluationRequest
  ): Promise<Evaluation> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    if (!assignment) {
      throw new EvaluatorServiceError('Asignación no encontrada', 404);
    }
    const ts = new Date().toISOString();
    const evaluation: StoredEvaluation = {
      id: newId('eval'),
      assignmentId: assignment.id,
      applicationId,
      evaluatorId,
      criteriaEvaluations: evaluationData.criteriaEvaluations as any,
      budgetComments: evaluationData.budgetComments,
      overallComments: evaluationData.overallComments,
      conflictsDeclared: evaluationData.conflictsDeclared,
      conflictDetails: evaluationData.conflictDetails,
      submittedAt: null,
      createdAt: ts,
      updatedAt: ts,
    };
    db.evaluations.push(evaluation);
    persist();
    return delay(toEvaluation(evaluation), 200);
  }

  async updateEvaluation(
    applicationId: string,
    evaluationData: CreateEvaluationRequest
  ): Promise<Evaluation> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    const evaluation = assignment
      ? db.evaluations.find((e) => e.assignmentId === assignment.id)
      : undefined;
    if (!evaluation) {
      // No evaluation yet — create one instead of failing.
      return this.createEvaluation(applicationId, evaluationData);
    }
    evaluation.criteriaEvaluations = evaluationData.criteriaEvaluations as any;
    evaluation.budgetComments = evaluationData.budgetComments;
    evaluation.overallComments = evaluationData.overallComments;
    evaluation.conflictsDeclared = evaluationData.conflictsDeclared;
    evaluation.conflictDetails = evaluationData.conflictDetails;
    evaluation.updatedAt = new Date().toISOString();
    persist();
    return delay(toEvaluation(evaluation), 200);
  }

  async submitEvaluation(applicationId: string): Promise<SubmitEvaluationResponse> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    const evaluation = assignment
      ? db.evaluations.find((e) => e.assignmentId === assignment.id)
      : undefined;
    if (!assignment || !evaluation) {
      throw new EvaluatorServiceError('No hay evaluación para enviar', 404);
    }
    const ts = new Date().toISOString();
    evaluation.submittedAt = ts;
    evaluation.updatedAt = ts;
    assignment.status = 'completed';
    assignment.evaluationSubmittedAt = ts;
    persist();
    return delay({
      message: 'Evaluación enviada correctamente.',
      evaluationId: evaluation.id,
      submittedAt: ts,
    });
  }

  async setupPassword(
    request: SetupPasswordRequest
  ): Promise<PasswordSetupResponse> {
    const user = db.users.find(
      (u) => u.email.toLowerCase() === request.email.toLowerCase()
    );
    if (user) {
      user.isConfirmed = true;
      persist();
    }
    return delay({
      message: 'Contraseña configurada correctamente.',
      accountConfirmed: true,
    });
  }

  async requestPasswordSetup(
    _request: RequestPasswordSetupRequest
  ): Promise<RequestPasswordSetupResponse> {
    return delay({
      message: 'Si la cuenta existe, recibirás un correo para configurar tu contraseña.',
    });
  }

  async declineAssignment(
    applicationId: string,
    request: DeclineAssignmentRequest
  ): Promise<void> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    if (!assignment) throw new EvaluatorServiceError('Asignación no encontrada', 404);
    applyDeclineToAssignment(assignment, request.reason, request.note);
    persist();
    return delay(undefined as any);
  }

  /**
   * Resolves an invitation landing by its opaque token (the assignment id in
   * this mock). Reachable without login from the invitation email, so member
   * emails and other contact data are deliberately omitted.
   */
  async getInvitation(assignmentToken: string): Promise<InvitationDetails> {
    const assignment = db.assignments.find((a) => a.id === assignmentToken);
    const app = assignment ? findApp(assignment.applicationId) : undefined;
    const evaluator = assignment ? findUser(assignment.evaluatorId) : undefined;
    if (!assignment || !app || !evaluator) {
      throw new EvaluatorServiceError(
        'Invitación no encontrada o enlace inválido',
        404
      );
    }

    const teamSection = app.sectionAnswers?.PROJECT_TEAM;
    const members = [
      teamSection?.principalInvestigator,
      ...(teamSection?.coInvestigators ?? []),
    ].filter(Boolean);
    const team = members.map((m: any) => ({
      name: m.name,
      role: m.role,
      affiliation: m.affiliation,
      institutionType: m.institutionType,
      country: m.country,
    }));

    // El árbitro (3.º evaluador de desempate) NO debe saber que lo es: la
    // invitación que ve es idéntica a la de un evaluador primario. La detección
    // de arbitraje y sus puntajes previos viven solo del lado admin; aquí no se
    // exponen. Ver docs/AJUSTES_REUNION_2026-07-08.md (Precaución #2, L1).
    return delay({
      assignmentId: assignment.id,
      applicationId: assignment.applicationId,
      applicationNumber: app.applicationNumber,
      projectTitle: projectTitleOf(assignment.applicationId),
      executiveSummary:
        app.sectionAnswers?.PROJECT_DESCRIPTION?.executiveSummary ??
        'Sin resumen disponible',
      team,
      assignedAt: assignment.assignedAt,
      status: assignment.status,
      evaluator: {
        id: evaluator.id,
        name: evaluator.name,
        email: evaluator.email,
        isConfirmed: evaluator.isConfirmed,
        isExternal: evaluator.isExternal,
      },
    });
  }

  async respondToInvitation(
    assignmentToken: string,
    request: InvitationResponseRequest
  ): Promise<InvitationResponseResponse> {
    const assignment = db.assignments.find((a) => a.id === assignmentToken);
    if (!assignment) {
      throw new EvaluatorServiceError(
        'Invitación no encontrada o enlace inválido',
        404
      );
    }
    if (assignment.status !== 'pending') {
      throw new EvaluatorServiceError('Esta invitación ya fue respondida.', 409);
    }
    const evaluator = findUser(assignment.evaluatorId);

    if (request.action === 'accept') {
      // Accepting from the landing doubles as the explicit no-conflict
      // declaration: the evaluator saw the full team before deciding.
      assignment.status = 'accepted_not_started';
      assignment.conflictDeclared = false;
      assignment.conflictStatus = 'no_conflict';
      assignment.conflictDeclaredAt = new Date().toISOString();
    } else {
      if (!request.reason) {
        throw new EvaluatorServiceError(
          'Debe indicar un motivo para declinar.',
          400
        );
      }
      applyDeclineToAssignment(assignment, request.reason, request.note);
    }
    persist();
    return delay({
      assignmentId: assignment.id,
      status: assignment.status,
      evaluatorConfirmed: evaluator?.isConfirmed ?? true,
      message:
        request.action === 'accept'
          ? 'Invitación aceptada.'
          : 'Respuesta registrada. Gracias por informarnos.',
    });
  }

  async declareConflictStatus(
    applicationId: string,
    request: ConflictDeclarationRequest
  ): Promise<ConflictDeclarationResponse> {
    const evaluatorId = currentEvaluatorId();
    const assignment = findAssignment(applicationId, evaluatorId);
    const app = findApp(applicationId);
    if (!assignment || !app) {
      throw new EvaluatorServiceError('Asignación no encontrada', 404);
    }
    const ts = new Date().toISOString();
    assignment.conflictDeclared = request.hasConflict;
    assignment.conflictStatus = request.hasConflict ? 'conflict_declared' : 'no_conflict';
    assignment.conflictDetails = request.conflictDetails ?? null;
    assignment.conflictDeclaredAt = ts;
    if (request.hasConflict) {
      assignment.status = 'conflict_declared';
    }
    persist();
    return delay({
      assignmentId: assignment.id,
      applicationId,
      applicationNumber: app.applicationNumber,
      conflictDeclared: request.hasConflict,
      conflictDetails: request.conflictDetails ?? null,
      conflictDeclaredAt: ts,
      status: assignment.status,
      message: request.hasConflict
        ? 'Conflicto de interés declarado.'
        : 'Declaración registrada: sin conflicto de interés.',
    });
  }
}

const evaluatorService = new EvaluatorService();
export default evaluatorService;
export { EvaluatorServiceError };
